<script setup lang="ts">
import type { AppNotification, NotificationTone } from "@/stores/notifications";
import { useNotificationsStore } from "@/stores/notifications";

const notifications = useNotificationsStore();

const notificationSymbols: Record<NotificationTone, string> = {
  success: "✓",
  info: "i",
  warning: "!",
  error: "!",
};

function roleFor(notification: AppNotification): "alert" | "status" {
  return notification.tone === "error" ? "alert" : "status";
}

function liveModeFor(notification: AppNotification): "assertive" | "polite" {
  return notification.tone === "error" ? "assertive" : "polite";
}

function resumeAfterFocus(event: FocusEvent, notificationId: string): void {
  const currentTarget = event.currentTarget;
  const nextTarget = event.relatedTarget;
  if (
    currentTarget instanceof HTMLElement &&
    nextTarget instanceof Node &&
    currentTarget.contains(nextTarget)
  ) {
    return;
  }
  notifications.resume(notificationId, "focus");
}
</script>

<template>
  <aside class="notification-region" aria-label="Application notifications">
    <TransitionGroup name="notification" tag="div" class="notification-stack">
      <article
        v-for="notification in notifications.visibleNotifications"
        :key="notification.id"
        class="app-notification"
        :class="`app-notification-${notification.tone}`"
        :role="roleFor(notification)"
        :aria-live="liveModeFor(notification)"
        aria-atomic="true"
        @mouseenter="notifications.pause(notification.id, 'hover')"
        @mouseleave="notifications.resume(notification.id, 'hover')"
        @focusin="notifications.pause(notification.id, 'focus')"
        @focusout="resumeAfterFocus($event, notification.id)"
      >
        <span class="notification-symbol" aria-hidden="true">
          <span v-if="notification.busy" class="notification-spinner"></span>
          <template v-else>{{ notificationSymbols[notification.tone] }}</template>
        </span>

        <div class="notification-copy">
          <strong>{{ notification.title }}</strong>
          <p v-if="notification.message">{{ notification.message }}</p>
          <button
            v-if="notification.actionLabel"
            class="notification-action"
            type="button"
            :disabled="notification.actionPending"
            @click="notifications.runAction(notification.id)"
          >
            {{ notification.actionPending ? "Working…" : notification.actionLabel }}
          </button>
        </div>

        <button
          class="notification-dismiss"
          type="button"
          :aria-label="`Dismiss ${notification.title} notification`"
          @click="notifications.dismiss(notification.id)"
        >
          <span aria-hidden="true">×</span>
        </button>
      </article>
    </TransitionGroup>

    <p v-if="notifications.queuedCount > 0" class="sr-only" role="status" aria-live="polite">
      {{ notifications.queuedCount }} more
      {{ notifications.queuedCount === 1 ? "notification is" : "notifications are" }} queued.
    </p>
  </aside>
</template>
